import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { GoogleButton } from "@/components/GoogleButton";
import { TwoFactorChallenge } from "@/components/TwoFactorChallenge";

export default function Login() {
  const { login, loginWithData } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.requires_2fa) { setChallenge(res.challenge_token); return; }
      toast.success("Welcome back.");
      nav("/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (challenge) {
    return (
      <AuthShell>
        <TwoFactorChallenge challengeToken={challenge} onSuccess={(data) => { loginWithData(data); toast.success("Welcome back."); nav("/app"); }} />
        <button onClick={() => setChallenge(null)} className="text-sm text-muted-foreground hover:text-foreground mt-6 inline-flex items-center gap-1.5" data-testid="twofa-cancel">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="back-home">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <h1 className="font-serif text-3xl font-semibold">Sign in</h1>
      <p className="text-muted-foreground text-sm mt-2">Access your legal intelligence workspace.</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <Label htmlFor="email">Work email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5" placeholder="you@company.com" data-testid="login-email" />
        </div>
        <div>
          <div className="flex items-center justify-between"><Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-brand" data-testid="to-forgot">Forgot password?</Link></div>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 rounded-none" placeholder="••••••••" data-testid="login-password" />
        </div>
        <Button type="submit" className="w-full rounded-none" disabled={loading} data-testid="login-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-border" />
        <span className="overline text-muted-foreground">or</span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton label="Sign in with Google" />

      <p className="text-sm text-muted-foreground mt-6">
        No account? <Link to="/register" className="text-foreground underline underline-offset-4" data-testid="to-register">Request access</Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm fade-up">{children}</div>
      </div>
      <div className="hidden lg:block relative border-l border-border">
        <img src="/hero-monolith.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-[70%_center]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <Logo textClass="text-white" />
          <p className="font-serif text-2xl mt-6 max-w-md leading-snug">
            "Clear jurisdiction separation. Source-based answers. Enterprise-grade security."
          </p>
          <p className="text-white/70 text-sm mt-3">The founding principles of Legalis AI.</p>
        </div>
      </div>
    </div>
  );
}
